/**
 * Whether a completed itinerary save is something guests need to hear about,
 * and if so, what to say.
 *
 * The product rule (verbatim from the owner): Studio must ASK the host before
 * telling guests anything, never auto-send. "ask the host if they want to send
 * an update when a change is made vs. someone accidentally changing something
 * then the whole group auto getting a blast." This module only decides WHETHER
 * there is something worth asking about and drafts the copy; it never sends
 * anything itself — see `components/groups/changes/ChangeNoticeDialog.tsx` for
 * the two-step confirmation that actually calls the broadcast API.
 *
 * Pure and side-effect free on purpose: the itinerary tab calls this AFTER a
 * save has already succeeded, comparing the tile as it was before the save to
 * what the server just echoed back. Nothing here re-derives visibility rules —
 * `itineraryReach` (lib/itineraryVisibility.ts) is the single source of truth
 * for "can a guest see this at all," and this module only asks whether that
 * answer, or the handful of fields guests would notice, changed.
 *
 * Two drafts come out of every change: an email (nicer typography is fine) and
 * an SMS (plain GSM-7 and compact — a single "→" turns it into a UCS-2 text
 * that is over the 4-segment limit and cannot be sent at all).
 */
import { itineraryAudience, itineraryReach, itineraryStatus } from './itineraryVisibility';
import { zoneCity } from './rezoneItinerary';
import { toSmsSafe } from './smsSafeText';
import type { GroupItineraryItem } from '../types/groups';

export type ItineraryChangeKind =
  | 'first_publish'
  | 'cancelled'
  | 'time_change'
  | 'location_change'
  | 'title_change'
  | 'multiple';

/**
 * What ChangeNoticeDialog needs to offer a notice: the words, and nothing about
 * where they came from. An itinerary change and a dinner change both produce one.
 */
export type ChangeNoticeDraft = Pick<
  ItineraryChangeNotice,
  'summary' | 'subject' | 'emailBody' | 'smsText' | 'smsWarning'
>;

export interface ItineraryChangeNotice {
  kind: ItineraryChangeKind;
  /** One plain-English sentence, old -> new, in the trip zone. Shown in the dialog. */
  summary: string;
  /** Individual field changes, old -> new. Empty for first_publish. */
  changes: string[];
  /** Suggested copy, prefilled into the dialog's editable fields. */
  subject: string;
  emailBody: string;
  /**
   * <= 280 chars, plain GSM-7 (see lib/smsSafeText.ts), no link — the concierge
   * appends its own heading and the trip link.
   */
  smsText: string;
  /** Set when drafting the SMS had to replace a character it could not fold; the host should read it. */
  smsWarning: string | null;
}

export const SMS_MAX = 280;

function reach(status: ReturnType<typeof itineraryStatus>, audience: ReturnType<typeof itineraryAudience>) {
  return itineraryReach(status, audience);
}

function isUsableZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

export interface MomentParts {
  /** "Fri, Sep 26" */
  date: string;
  /** "7:00" */
  clock: string;
  /** "PM" */
  period: string;
  /** "7:00 PM" */
  time: string;
}

/** Date and clock, read in the given zone (never UTC, never the browser's). */
export function momentParts(iso: string, zone: string): MomentParts {
  const d = new Date(iso);
  const date = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, weekday: 'short', month: 'short', day: 'numeric',
  }).format(d);
  // formatToParts rather than string surgery: newer ICU puts a narrow
  // no-break space before "PM", which would otherwise leak into the SMS.
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hour: 'numeric', minute: '2-digit', hour12: true,
  }).formatToParts(d);
  const pick = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  const clock = `${pick('hour')}:${pick('minute')}`;
  const period = pick('dayPeriod').toUpperCase();
  return { date, clock, period, time: `${clock} ${period}` };
}

/** "Fri, Sep 26, 7:00 PM (Europe/Lisbon)", read in the given zone. */
function formatMoment(iso: string, zone: string): string {
  const { date, time } = momentParts(iso, zone);
  return `${date}, ${time} (${zone})`;
}

/**
 * One start-to-end span as a clock range: "7:00–9:00 PM" when both ends share
 * an AM/PM, "11:00 AM–1:00 PM" when they do not, "7:00 PM" with no end, and
 * "11:00 PM–Sat, Sep 27, 1:00 AM" when the end is on a different local day.
 * `dash` and `dateOf` let the email and the SMS spell the same span their own way.
 */
function clockSpan(
  start: MomentParts,
  end: MomentParts | null,
  dash: string,
  dateOf: (parts: MomentParts) => string,
): string {
  if (!end) return start.time;
  if (end.date !== start.date) return `${start.time}${dash}${dateOf(end)}, ${end.time}`;
  return start.period === end.period
    ? `${start.clock}${dash}${end.clock} ${end.period}`
    : `${start.time}${dash}${end.time}`;
}

const emailDate = (parts: MomentParts) => parts.date;
/** "Fri Oct 16" — the SMS has no comma to spare and reads fine without one. */
export const smsDate = (parts: MomentParts) => parts.date.replace(',', '');

/** One field's change, spelled once for the email and once for the SMS. */
interface FieldChange {
  /** A line for the email body and the dialog: old -> new, with the zone once. */
  email: string;
  /** A compact fragment for the SMS: no zone, no arrows, no smart punctuation. */
  sms: string;
}

interface Slot {
  start: string;
  end: string | null;
}

/**
 * The start and end of a slot moving, as ONE change.
 *
 * Start and end used to be two lines ("Time: … → …; Ends: … → …") that each
 * repeated the date and the zone. It is one fact — the slot moved — so it is
 * one range on each side: "Fri, Oct 16, 7:00–9:00 PM → 8:00–10:00 PM
 * (Europe/Lisbon)". The date is read in the given zone, never UTC or the
 * browser's, because an instant that reads as "the same evening" in UTC can be
 * the next calendar day at the trip.
 */
function slotChange(before: Slot, after: Slot, zone: string): FieldChange {
  const bs = momentParts(before.start, zone);
  const as = momentParts(after.start, zone);
  const be = before.end ? momentParts(before.end, zone) : null;
  const ae = after.end ? momentParts(after.end, zone) : null;

  const emailBefore = clockSpan(bs, be, '–', emailDate);
  const emailAfter = clockSpan(as, ae, '–', emailDate);
  const email = bs.date === as.date
    ? `Time: ${bs.date}, ${emailBefore} → ${emailAfter} (${zone})`
    : `Time: ${bs.date}, ${emailBefore} → ${as.date}, ${emailAfter} (${zone})`;

  const smsBefore = clockSpan(bs, be, '-', smsDate);
  const smsAfter = clockSpan(as, ae, '-', smsDate);
  const sms = bs.date === as.date
    ? `${smsDate(as)}, now ${smsAfter} (was ${smsBefore})`
    : `now ${smsDate(as)}, ${smsAfter} (was ${smsDate(bs)}, ${smsBefore})`;
  return { email, sms };
}

/** Only the end moved. */
function endChange(beforeIso: string, afterIso: string, zone: string): FieldChange {
  const before = momentParts(beforeIso, zone);
  const after = momentParts(afterIso, zone);
  const same = before.date === after.date;
  return {
    email: same
      ? `Ends: ${before.date}, ${before.time} → ${after.time} (${zone})`
      : `Ends: ${before.date}, ${before.time} → ${after.date}, ${after.time} (${zone})`,
    sms: same
      ? `${smsDate(after)}, now ends ${after.time} (was ${before.time})`
      : `now ends ${smsDate(after)} ${after.time} (was ${smsDate(before)} ${before.time})`,
  };
}

/**
 * Two timestamps name the same moment. Compared as instants, not strings: the
 * list read and the save response are separate serializations, and
 * "…T18:00:00Z" vs "…T19:00:00+01:00" is the same event — a string compare
 * would prompt the host with "7:00 PM → 7:00 PM" on a save that moved nothing.
 */
function sameInstant(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return !a && !b;
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  if (Number.isNaN(ta) || Number.isNaN(tb)) return a === b;
  return ta === tb;
}

/**
 * The SMS draft: plain GSM-7 (see lib/smsSafeText.ts for why that matters) and
 * cut to the field's cap. Cut AFTER sanitising, because folding can lengthen a
 * character ("ß" → "ss", "→" → "->").
 */
export function smsDraft(text: string): { smsText: string; smsWarning: string | null } {
  const { text: safe, lossy } = toSmsSafe(text);
  const smsText = safe.length <= SMS_MAX ? safe : `${safe.slice(0, SMS_MAX - 3).trimEnd()}...`;
  return {
    smsText,
    smsWarning: lossy
      ? 'Some characters in the text message could not be sent as plain text and now show as "?". '
        + 'Fix them before sending.'
      : null,
  };
}

function guestVisible(item: GroupItineraryItem | null): boolean {
  if (!item) return false;
  return reach(itineraryStatus(item), itineraryAudience(item)) !== 'nobody';
}

/**
 * Compares an itinerary tile before and after a successful save and decides
 * whether guests should be told, and if so, drafts the copy.
 *
 * `before` is null for a brand-new tile. `tripTimeZone` is the trip's own
 * resolved zone (see `resolveTripTimeZone` in lib/tripTime.ts) — used only when
 * the tile itself carries no zone override.
 *
 * Returns null for anything guests would not notice:
 *  - a draft that stays a draft (or private that stays private)
 *  - a description-only, display-order-only, or no-op edit
 *  - any edit to a tile that was never guest-visible and still is not
 */
export function buildItineraryChangeNotice(
  before: GroupItineraryItem | null,
  after: GroupItineraryItem,
  tripTimeZone: string,
): ItineraryChangeNotice | null {
  const zoneCandidate = after.timeZone || before?.timeZone || tripTimeZone;
  const zone = isUsableZone(zoneCandidate) ? zoneCandidate : 'UTC';
  const afterStatus = itineraryStatus(after);
  const wasVisible = guestVisible(before);
  const isVisible = guestVisible(after);
  const title = after.title || before?.title || 'This plan';

  // Cancellation: only worth a notice if guests could actually see the plan
  // being cancelled. A cancelled draft-that-was-private never reached anyone.
  if (afterStatus === 'cancelled' && wasVisible) {
    const reason = after.cancellationReason ? ` Reason: ${after.cancellationReason}` : '';
    const summary = `"${title}" has been cancelled.${reason}`;
    return {
      kind: 'cancelled',
      summary,
      changes: [`Cancelled${after.cancellationReason ? `: ${after.cancellationReason}` : ''}`],
      subject: `Change to ${title}`,
      emailBody:
        `${title} has been cancelled.${reason}\n\n`
        + 'See the full, up-to-date schedule here: {url}',
      ...smsDraft(`${title} has been cancelled.${reason}`),
    };
  }

  // First publish: the tile was invisible to every guest (draft, or private)
  // and now is not. Whatever fields also changed on the way in don't matter —
  // there is nothing for a guest to compare against, only a new entry.
  if (!wasVisible && isVisible && afterStatus !== 'cancelled') {
    const when = after.startsAt ? formatMoment(after.startsAt, zone) : null;
    const summary = when
      ? `New on the schedule: "${title}" — ${when}.`
      : `New on the schedule: "${title}".`;
    let smsWhen = '';
    if (after.startsAt) {
      const moment = momentParts(after.startsAt, zone);
      smsWhen = ` - ${smsDate(moment)}, ${moment.time}`;
    }
    return {
      kind: 'first_publish',
      summary,
      changes: [],
      subject: `New on the schedule: ${title}`,
      emailBody: `${title} was just added to the schedule${when ? `, ${when}` : ''}.\n\n`
        + 'See the full schedule here: {url}',
      ...smsDraft(`New on the schedule: ${title}${smsWhen}.`),
    };
  }

  // Neither before nor after is guest-visible, or the tile was hidden and
  // stays hidden — nothing for a guest to have noticed.
  if (!wasVisible && !isVisible) return null;
  // The tile was visible and was made invisible (unpublished, or narrowed to
  // hosts-only). Not one of the prompt-worthy transitions the product asked
  // for, and there is no "un-tell" a guest who already saw it.
  if (wasVisible && !isVisible) return null;
  if (afterStatus === 'cancelled') return null; // wasVisible was already false, handled above

  if (!before) return null; // defensive — reached only if wasVisible was somehow true with no before

  const fields: FieldChange[] = [];
  const noted: ItineraryChangeKind[] = [];
  const note = (next: ItineraryChangeKind) => {
    noted.push(next);
  };

  const startChanged = !sameInstant(before.startsAt, after.startsAt);
  const endChanged = !sameInstant(before.endsAt, after.endsAt);
  const locationChanged = (before.locationName || '') !== (after.locationName || '');
  const titleChanged = (before.title || '') !== (after.title || '');

  // Start and end moving together is one range change, not two lines.
  let endHandled = false;
  if (startChanged && before.startsAt && after.startsAt) {
    fields.push(slotChange(
      { start: before.startsAt, end: before.endsAt || null },
      { start: after.startsAt, end: after.endsAt || null },
      zone,
    ));
    endHandled = true;
    note('time_change');
  } else if (startChanged) {
    const moment = after.startsAt ? momentParts(after.startsAt, zone) : null;
    fields.push({
      email: after.startsAt ? 'A start time was added.' : 'The start time was removed.',
      sms: moment ? `start time added (${smsDate(moment)}, ${moment.time})` : 'start time removed',
    });
    note('time_change');
  }
  if (endChanged && !endHandled) {
    if (before.endsAt && after.endsAt) {
      fields.push(endChange(before.endsAt, after.endsAt, zone));
    } else {
      const moment = after.endsAt ? momentParts(after.endsAt, zone) : null;
      fields.push({
        email: after.endsAt ? 'An end time was added.' : 'The end time was removed.',
        sms: moment ? `end time added (${moment.time})` : 'end time removed',
      });
    }
    note('time_change');
  }
  if (locationChanged) {
    const was = before.locationName || 'TBD';
    const now = after.locationName || 'TBD';
    fields.push({
      email: `Location: ${was} → ${now}`,
      sms: `now at ${now} (was ${was})`,
    });
    note('location_change');
  }
  if (titleChanged) {
    const was = before.title || 'Untitled';
    const now = after.title || 'Untitled';
    fields.push({
      email: `Title: ${was} → ${now}`,
      sms: `renamed from ${was}`,
    });
    note('title_change');
  }

  // Nothing a guest would notice changed (description, display order, CTA,
  // place snapshot fields, audience within "still visible", etc.).
  if (noted.length === 0 || fields.length === 0) return null;
  const kind: ItineraryChangeKind = noted.length > 1 ? 'multiple' : noted[0];

  const changes = fields.map((field) => field.email);
  const headline = titleChanged ? `"${before.title}" is now "${after.title}"` : `"${title}" changed`;
  const summary = `${headline}: ${changes.join('; ')}`;

  let smsSentence: string;
  if (kind === 'title_change') {
    smsSentence = `${before.title || 'Untitled'} is now called ${after.title || 'Untitled'}.`;
  } else {
    const verb = kind === 'time_change' ? 'has moved' : 'has changed';
    smsSentence = `${title} ${verb}: ${fields.map((field) => field.sms).join('; ')}.`;
  }

  return {
    kind,
    summary,
    changes,
    subject: `Change to ${before.title || title}`,
    emailBody: `${headline}.\n\n${changes.join('\n')}\n\n`
      + 'See the full, up-to-date schedule here: {url}',
    ...smsDraft(smsSentence),
  };
}

/** One event that was just re-zoned: the tile as it was, and where it landed. Structurally a RezoneMove. */
export interface RezonedTile {
  /** The tile BEFORE the move; its status/audience are unchanged by it. */
  item: GroupItineraryItem;
  toZone: string;
  /** New instants, offset-qualified against `toZone`. */
  startsAt: string | null;
  endsAt: string | null;
}

/**
 * The offer that follows a re-zone (lib/rezoneItinerary). Moving keeps each
 * event's local time but changes the zone guests read beside it ("8:00 PM ·
 * America/Chicago" becomes "8:00 PM · Europe/Lisbon"), which is a visible change.
 *
 * Only tiles a guest can see count (`guestVisible`, i.e. itineraryReach): a
 * draft or hosts-only tile changing zone is nothing anyone was told about.
 * Returns null when none of the moved tiles is guest-visible.
 *
 * Like every notice here this only DRAFTS; ChangeNoticeDialog asks the host and
 * "Don't send" is its default. Nothing in this function sends.
 */
export function buildRezoneNotice(
  moves: readonly RezonedTile[],
  tripZone: string,
): ItineraryChangeNotice | null {
  const visible = moves.filter((move) => guestVisible(move.item));
  if (visible.length === 0) return null;

  const zone = isUsableZone(tripZone) ? tripZone : 'UTC';
  const city = zoneCity(zone);
  const head = `Times now shown in ${city} time: `;

  const lines: string[] = [];
  const parts: string[] = [];
  for (const move of visible) {
    const title = (move.item.title || '').trim() || 'This plan';
    const startIso = move.startsAt || move.endsAt;
    if (!startIso) continue;
    const start = momentParts(startIso, zone);
    const end = move.startsAt && move.endsAt ? momentParts(move.endsAt, zone) : null;
    const was = move.item.timeZone ? `, previously shown in ${move.item.timeZone}` : '';
    lines.push(`- ${title}: ${start.date}, ${clockSpan(start, end, '–', emailDate)} (${zone})${was}`);
    parts.push(`${title}, ${smsDate(start)}, ${start.time}`);
  }
  if (parts.length === 0) return null;

  // Fit as many events as the SMS cap allows and say how many did not fit,
  // rather than let a blunt cut drop the tail mid-word.
  const composeSms = (n: number) => `${head}${parts.slice(0, n).join('; ')}`
    + `${n < parts.length ? `; and ${parts.length - n} more` : ''}.`;
  let shown = parts.length;
  while (shown > 1 && toSmsSafe(composeSms(shown)).text.length > SMS_MAX) shown -= 1;

  const count = parts.length;
  return {
    kind: 'multiple',
    summary: `${head}${parts.join('; ')}.`,
    changes: lines.map((line) => line.replace(/^- /, '')),
    subject: `Times now shown in ${city} time`,
    emailBody:
      `${count === 1 ? 'The time for one plan is' : `The times for ${count} plans are`} now shown in `
      + `${city} time (${zone}):\n\n${lines.join('\n')}\n\n`
      + 'See the full, up-to-date schedule here: {url}',
    ...smsDraft(composeSms(shown)),
  };
}
